
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';

import { Observable, throwError, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AppStorageService, StorageKey } from './app-storage.service';
import { LocalCacheService } from '../core/services/local-cache.service';
import { TelemetryService } from '../core/services/telemetry.service';


export interface DeviceIdentityModel {
  deviceId: string;
  status: string | null;
  authenticationType?: string | null;
  lastActivityTime?: string | null;
  connectionState?: string | null;
}

export interface DeviceTwinModel {
  deviceId: string;
  etag?: string;
  deviceEtag?: string;
  version?: number;
  properties?: Record<string, unknown>;
  capabilities?: Record<string, unknown>;
  modelId?: string;
  status?: string;
  statusUpdateTime?: string;
  lastActivityTime?: string;
  authenticationType?: string;
  connectionState?: string;
  cloudToDeviceMessageCount?: number;
  iothubDetails?: Record<string, unknown>;
}

export interface DirectMethodRequest {
  method_name: string;
  payload?: Record<string, unknown>;
  timeout?: number;
  connect_timeout?: number;
}

export interface DirectMethodResponse {
  deviceId: string;
  status?: number;
  payload?: unknown;
}

export interface C2DSendRequest {
  message: string;
}

export interface C2DSendResponse {
  deviceId: string;
  message: string;
}

export interface DeviceStackInfo {
  deviceUid: string;
  stack: string;
  success: boolean;
  message: string;
}

export interface BatchStackResponse {
  success: boolean;
  devices: DeviceStackInfo[];
}

/** DeviceService GET /devices/api/stack/available DTOs used by getStackList(). */
export interface AvailableStackDto {
  name: string;
}

export interface AvailableStackListResponse {
  success: boolean;
  errors?: string[];
  data: AvailableStackDto[];
}

/** DeviceService POST /devices/api/stack/switch result DTO for stack switch operations. */
export interface StackSwitchResultDto {
  deviceUid: string;
  previousStack: string;
  newStack: string;
  softResetRequired: boolean;
  softResetSent: boolean | null;
}

export interface StackSwitchResponse {
  success: boolean;
  errors: string[];
  data: StackSwitchResultDto;
}

/** DeviceService GET /devices/api/stack/{deviceUid} current stack DTO. */
export interface StackInfoDto {
  deviceUid: string;
  stackName: string;
}

export interface StackInfoResponse {
  success: boolean;
  errors: string[];
  data: StackInfoDto;
}

export interface DashboardPage {
  items: DeviceRow[];
  pageSize: number;
  page?: number;
  nextCursor?: string | null;
  hasMore: boolean;
  total?: number;
}

export interface DeviceRow {
  // Device identification and version info
  deviceId: string;                              // UID from backend
  f_VER: string | null;                          // F_VER from deviceDetails
  coN_FVER: string | null;                       // CON_FVER from deviceDetails
  IoTHubName: string | null;                     // IoTHubName from properties.reported
  deviceTypeName: string | null;                 // Device type name from backend enrichment
  
  // Connection and status
  connectionState: 'Connected' | 'Disconnected'; // 0 = Disconnected, 1 = Connected
  lastActivityTime: string | null;               // ISO timestamp from backend
  status: string | null;                         // 0 = Enabled, 1 = Disabled
  environment: string | null;                    // Extracted from IoTHubName (dev/prod/qa)
  currentStack: string | null;                   // Current stack the device is connected to
  
  // Keep these for backward compatibility
  lastSeen: string | null;
  lastSeenUnixMs: number | null;
}
 

export interface RowsPayload {
  rows: DeviceRow[];
  total?: number;
  pageSize?: number;
  page?: number;
  hasMore?: boolean;
  nextCursor?: string | null;
}

export type DashboardResponse = DashboardPage | RowsPayload | DeviceRow[];
export type TelemetryProperties = Record<string, unknown>;

/** Response shape from the DiagnosticService notifications API. */
export interface NotificationApiResponse {
  notifications: Record<string, unknown>[];
  caller_uid: string;
  continuation_token?: string | null;
  total_count?: number | null;
}

export interface TelemetryMessage {
  partition: string;
  deviceId?: string;
  device_id?: string;
  enqueuedTime: string;
  enqueued_time?: string;

  payload: Record<string, unknown>;
  properties?: TelemetryProperties;
  
  /** Unique key for tracking/state management (assigned on creation) */
  _key: string;
  
  [key: string]: unknown;
}

export interface TelemetryResponse {
  deviceId?: string;
  messages: TelemetryMessage[];
  [key: string]: unknown;
}

// Decrypt API interfaces
export interface DecryptRequest {
  payload: string;
  deviceType: string;
  format?: 'hex' | 'base64' | 'framed_hex';
  escByteCount?: number;
}

export interface DecryptResponse {
  success: boolean;
  // API returns decryptedData (JSON string with DATA field)
  decryptedData?: string | null;
  decrypted_data?: string | null;
  // Legacy fields (may not be used)
  decrypted?: string;
  decrypted_hex?: string;
  decryptedHex?: string;
  decryptedText?: string | null;
  decrypted_text?: string | null;
  deviceType?: string;
  device_type?: string;
  error?: string | null;
}

// ==== Exception (App Insights) interfaces ====

export interface ExceptionRecord {
  timestamp: string;
  service_name: string;
  exception_type: string | null;
  outer_message: string | null;
  severity_level: number | null;
  operation_id: string | null;
  operation_name: string | null;
  correlation_id: string | null;
  device_id: string | null;
  assembly: string | null;
  stack_trace: string | null;
  custom_dimensions: Record<string, unknown>;
}

export interface ExceptionQueryParams {
  device_id?: string;
  environment: 'dev' | 'qa' | 'prod';
  from_time: string;
  to_time: string;
  limit: number;
}

export interface ExceptionStreamInit    { total_services: number; }
export interface ExceptionStreamChunk   { service_name: string; exceptions: ExceptionRecord[]; }
export interface ExceptionStreamComplete { total_count: number; services_queried: string[]; services_failed: string[]; }
export type ExceptionStreamEventType = 'init' | 'chunk' | 'complete';
export interface ExceptionStreamEvent   { type: ExceptionStreamEventType; data: ExceptionStreamInit | ExceptionStreamChunk | ExceptionStreamComplete; }

export interface ExceptionTraceStep {
  timestamp: string;
  item_type: string;
  name: string;
  duration_ms: number | null;
  message: string | null;
  properties: Record<string, unknown>;
}

export interface ExceptionTraceResponse {
  operation_id: string;
  environment: string;
  steps: ExceptionTraceStep[];
}

@Injectable({ providedIn: 'root' })
export class DeviceService {
  private readonly baseUrl = environment.apiOrigin;
  private readonly diagnosticUrl = environment.diagnosticApiOrigin;
  private readonly decryptUrl = environment.decryptApiOrigin;
  constructor(
    private https: HttpClient,
    private storage: AppStorageService,
    private cache: LocalCacheService,
    private telemetry: TelemetryService,
  ) { }

  /**
   * Get query parameters for backend routing (environment and hubName)
   */
  private getContextParams(additionalParams?: HttpParams): HttpParams {
    let params = additionalParams || new HttpParams();
    
    const env = this.storage.getString(StorageKey.CURRENT_ENVIRONMENT, 'session');
    const hubName = this.storage.getString(StorageKey.CURRENT_HUB_NAME, 'session');
    
    if (env) {
      params = params.set('environment', env);
    }
    if (hubName) {
      params = params.set('hubName', hubName);
    }
    
    return params;
  }

  private handleError(err: HttpErrorResponse) {
    const detail =
      (err.error && (err.error.detail ?? err.error.message)) ||
      err.message ||
      'Unknown error';
    return throwError(() => new Error(detail));
  }

  // ==== Device Types ====
  
  /**
   * Fetch all device types from the backend.
   * Returns array of { id, uid, name, ... } objects.
   */
  getDeviceTypes(): Observable<any[]> {
    const url = environment.deviceTypesUrl;
    return this.https.get<any>(url).pipe(
      map((res: any) => {
        // Handle different response formats
        if (res?.success && Array.isArray(res?.data)) {
          return res.data;
        }
        if (Array.isArray(res)) {
          return res;
        }
        return [];
      }),
      catchError(e => {
        return of([]);
      })
    );
  }

  // ==== Dashboard ====

  getDashboard(page: number, pageSize: number, q?: string): Observable<DashboardResponse> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));

    const query = (q ?? '').trim();
    if (query.length >= 1) {
      params = params.set('q', query);
    }

    // Use the correct backend endpoint for devices list
    const url = `${environment.iothubBaseUrl}`;
    
    return this.https.get<DashboardResponse>(url, { params }).pipe(
      catchError(e => this.handleError(e))
    );
  }


  // ==== Identity ====
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  private static readonly MAX_CACHE_SIZE = 100;
  
  private identityCache = new Map<string, { obs: Observable<DeviceIdentityModel>; timestamp: number }>();

  private cleanExpiredCache(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];
    
    this.identityCache.forEach((entry, key) => {
      if (now - entry.timestamp > DeviceService.CACHE_TTL_MS) {
        expiredKeys.push(key);
      }
    });
    
    expiredKeys.forEach(key => this.identityCache.delete(key));
    
    // If still over limit, remove oldest entries
    if (this.identityCache.size > DeviceService.MAX_CACHE_SIZE) {
      const entries = Array.from(this.identityCache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toRemove = entries.slice(0, this.identityCache.size - DeviceService.MAX_CACHE_SIZE);
      toRemove.forEach(([key]) => this.identityCache.delete(key));
    }
  }

  clearIdentityCache(): void {
    this.identityCache.clear();
  }

  getIdentity(deviceId: string): Observable<DeviceIdentityModel> {
    const id = (deviceId ?? '').trim();
    if (!id) {
      return throwError(() => new Error('deviceId is required'));
    }
    
    // Clean expired entries periodically
    this.cleanExpiredCache();
    
    const cached = this.identityCache.get(id);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp < DeviceService.CACHE_TTL_MS)) {
      return cached.obs;
    }

    const url = `${this.diagnosticUrl}/diagnostics/api/v1/devices/${encodeURIComponent(id)}`;

    const obs = this.https.get<DeviceIdentityModel>(url, {
      params: this.getContextParams()
    }).pipe(
      tap({
        error: () => {
          this.identityCache.delete(id);
        },
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError(e => this.handleError(e))
    );

    this.identityCache.set(id, { obs, timestamp: now });
    return obs;
  }

  // ==== TWIN ====
  getTwin(deviceId: string): Observable<DeviceTwinModel> {
    const url = `${this.diagnosticUrl}/diagnostics/api/v1/devices/${encodeURIComponent(deviceId)}/twin`;
    return this.https.get<DeviceTwinModel>(url, {
      params: this.getContextParams()
    }).pipe(
      catchError(e => this.handleError(e))
    );
  }


  // ======= Update Twin ========
  updateTwin(deviceId: string, patch: any) {
    const url = `${this.diagnosticUrl}/diagnostics/api/v1/devices/${encodeURIComponent(deviceId)}/twin/update`;
    return this.https
      .patch<DeviceTwinModel>(url, patch, { params: this.getContextParams() })
      .pipe(catchError(e => this.handleError(e)));
  }


  // ==== DIRECT METHOD ====

  invokeDirectMethod(
    deviceId: string,
    body: DirectMethodRequest
  ) {
    const url = `${this.diagnosticUrl}/diagnostics/api/v1/direct-method/${encodeURIComponent(deviceId)}`;
    return this.https.post<DirectMethodResponse>(url, body, {
      headers: { 'Content-Type': 'application/json' },
      params: this.getContextParams()
    });
  }

  // ==== RESET DEVICE ====
  resetDevice(
    deviceId: string,
    hubName?: string | null,
    environment?: string | null,
  ): Observable<{ success: boolean; message?: string }> {
    const url = `${this.diagnosticUrl}/diagnostics/api/v1/devices/${encodeURIComponent(deviceId)}/reset`;
    let params = new HttpParams();
    if (environment) params = params.set('environment', environment);
    if (hubName) params = params.set('hubName', hubName);
    return this.https
      .post<{ success: boolean; message?: string }>(url, {}, { params })
      .pipe(catchError(e => this.handleError(e)));
  }


  // ==== STACK DEVICE ====
  stackDevice(deviceId: string, stack: string): Observable<StackSwitchResponse> {
    const url = `${this.baseUrl}/devices/api/stack/switch`;
    return this.https
      .post<StackSwitchResponse>(url, { deviceUid: deviceId, targetStack: stack }, {
        params: this.getContextParams()
      })
      .pipe(catchError(e => this.handleError(e)));
  }

  // ==== GET STACK LIST ====
  getStackList(): Observable<AvailableStackListResponse> {
    const url = `${this.baseUrl}/devices/api/stack/available`;
    return this.https
      .get<AvailableStackListResponse>(url, {
        params: this.getContextParams()
      })
      .pipe(catchError(e => this.handleError(e)));
  }

  getCurrentDeviceStack(deviceUid: string): Observable<StackInfoResponse> {
    const url = `${this.baseUrl}/devices/api/stack/${encodeURIComponent(deviceUid)}`;
    return this.https
      .get<StackInfoResponse>(url, {
        params: this.getContextParams()
      })
      .pipe(catchError(e => this.handleError(e)));
  }

  // ==== CLOUD-TO-DEVICE ====
  sendC2D(deviceId: string, message: string): Observable<C2DSendResponse> {
    const url = `${this.diagnosticUrl}/diagnostics/api/v1/c2d/${encodeURIComponent(deviceId)}`;
    const body: C2DSendRequest = { message };

    return this.https
      .post<C2DSendResponse>(url, body, {
        headers: { 'Content-Type': 'application/json' },
        params: this.getContextParams()
      })
      .pipe(
        catchError(e => this.handleError(e))
      );
  }

  // ==== TELEMETRY ====
  getTelemetry(deviceId?: string, limit: number = 50): Observable<TelemetryResponse> {
    const url = deviceId
      ? `${this.diagnosticUrl}/diagnostics/api/v1/telemetry/${encodeURIComponent(deviceId)}`
      : `${this.diagnosticUrl}/diagnostics/api/v1/telemetry`;

    const params = this.getContextParams(
      new HttpParams({ fromObject: { limit: String(limit) } })
    );

    return this.https.get<TelemetryResponse>(url, { 
      params,
      headers: {
        'OASIS-API-KEY': environment.oasisApiKey
      }
    }).pipe(
      catchError((err: HttpErrorResponse) =>
        throwError(
          () => new Error(err.error?.detail ?? err.error?.message ?? err.message ?? 'Unknown error')
        )
      )
    );
  }

  /**
   * Get telemetry history for a device within a date range
   * @param deviceId Device ID to fetch telemetry for
   * @param start Start datetime in format 'YYYY-MM-DD HH:MM:SS'
   * @param end End datetime in format 'YYYY-MM-DD HH:MM:SS'
   * @param iothubFolder IoT Hub folder name
   * @param limit Maximum number of records to fetch (optional)
   */
  getTelemetryHistory(
    deviceId: string,
    start: string,
    end: string,
    iothubFolder: string,
    limit?: number
  ): Observable<any> {
    const url = `${environment.diagnosticApiOrigin}/diagnostics/api/v1/device/telemetry`;
    
    let params = new HttpParams()
      .set('device_id', deviceId)
      .set('start', start)
      .set('end', end)
      .set('iothub_folder', iothubFolder);
    
    if (limit !== undefined && limit > 0) {
      params = params.set('limit', limit.toString());
    }

    return this.https.get<any>(url, {
      params: params,
      headers: {
        'OASIS-API-KEY': environment.oasisApiKey
      }
    }).pipe(
      catchError((err: HttpErrorResponse) =>
        throwError(
          () => new Error(err.error?.detail ?? err.error?.message ?? err.message ?? 'Failed to fetch telemetry history')
        )
      )
    );
  }

  // ==== DECRYPT PAYLOAD ====
  /**
   * Decrypt an encrypted payload using the backend crypto API
   * @param payload The encrypted payload (hex string or framed hex)
   * @param deviceType Device type for key selection (e.g., 'RXC', 'OPTIMUS', 'SCG', 'CLWIFI')
   * @param format Payload format: 'hex', 'base64', or 'framed_hex'
   */
  decryptPayload(
    payload: string,
    deviceType: string = 'RXC',
    format: 'hex' | 'base64' | 'framed_hex' = 'framed_hex'
  ): Observable<DecryptResponse> {
    // Always use the latest decrypt endpoint from environment
    const url = `${environment.decryptApiOrigin}/decrypt`;
    const body: DecryptRequest = {
      payload,
      deviceType,
      format,
      escByteCount: 0
    };

    return this.https
      .post<DecryptResponse>(url, body, {
        headers: {
          'Content-Type': 'application/json',
          'OASIS-API-KEY': environment.oasisApiKey
        }
      })
      .pipe(
        catchError((err: HttpErrorResponse) =>
          throwError(
            () => new Error(err.error?.detail ?? err.error?.error ?? err.message ?? 'Decryption failed')
          )
        )
      );
  }

  // ==== USER NOTIFICATIONS ====
  /**
   * Get user notifications for a given caller (device/user) with optional date range filtering.
   * Uses continuation-token pagination — pass the token from the previous response to fetch the next page.
   *
   * @param callerId The caller ID (e.g., device UID)
   * @param callerType Type of caller ('Device' or 'User')
   * @param limit Page size (default 500)
   * @param continuationToken Opaque offset token from a previous response (optional)
   * @param startTime ISO 8601 start time for filtering (optional)
   * @param endTime ISO 8601 end time for filtering (optional)
   */
  getUserNotifications(
    callerId: string,
    callerType: string = 'Device',
    limit: number = 500,
    continuationToken?: string,
    startTime?: string,
    endTime?: string
  ): Observable<NotificationApiResponse> {
    let params = new HttpParams()
      .set('limit', String(limit))
      .set('caller_type', callerType);

    if (startTime) {
      params = params.set('start_time', startTime);
    }
    if (endTime) {
      params = params.set('end_time', endTime);
    }

    let headers = new HttpHeaders();
    if (continuationToken) {
      headers = headers.set('X-Continuation-Token', continuationToken);
    }

    const url = `${environment.diagnosticApiOrigin}/diagnostics/api/v1/notifications/by-caller/${encodeURIComponent(callerId)}`;

    return this.https.get<NotificationApiResponse>(url, { params, headers }).pipe(
      catchError((err: HttpErrorResponse) =>
        throwError(
          () => new Error(err.error?.detail ?? err.error?.message ?? err.message ?? 'Failed to fetch notifications')
        )
      )
    );
  }

  /**
   * Fetch messages by device UID
   * @param deviceUId Device unique identifier
   * @param limit Maximum number of messages to fetch (default: 500)
   * @param continuationToken Opaque continuation token from a previous response (optional)
   * @param startTime ISO 8601 start time for filtering (optional)
   * @param endTime ISO 8601 end time for filtering (optional)
   * @param callerType Filter by caller type (optional)
   */
  getMessages(
    deviceUId: string,
    limit: number = 500,
    continuationToken?: string | null,
    startTime?: string,
    endTime?: string,
    callerType?: string
  ): Observable<any> {
    let params = new HttpParams().set('limit', String(limit));

    if (startTime) {
      params = params.set('start_time', startTime);
    }
    if (endTime) {
      params = params.set('end_time', endTime);
    }
    if (callerType) {
      params = params.set('caller_type', callerType);
    }

    let headers = new HttpHeaders();
    const normalizedContinuationToken = continuationToken?.trim();
    if (normalizedContinuationToken) {
      headers = headers.set('X-Continuation-Token', normalizedContinuationToken);
    }

    const encodedUId = encodeURIComponent(deviceUId.trim());
    const url = `${environment.diagnosticApiOrigin}/diagnostics/api/v1/messages/by-device/${encodedUId}`;

    return this.https.get<any>(url, { params, headers }).pipe(
      catchError((err: HttpErrorResponse) =>
        throwError(
          () => new Error(err.error?.detail ?? err.error?.message ?? err.message ?? 'Failed to fetch messages')
        )
      )
    );
  }

  // ==== EXCEPTIONS (App Insights) ====

  /**
   * Stream exceptions via Server-Sent Events from the DiagnosticService.
   *
   * Uses Angular HttpClient (with Fetch backend) so all interceptors — auth,
   * base-headers, retry, error-mapping — run on this request. No manual header
   * construction needed.
   *
   * Emits one ExceptionStreamEvent per SSE event:
   *   - {type:'init', data:{total_services:N}}
   *   - {type:'chunk', data:{service_name, exceptions[]}}
   *   - {type:'complete', data:{total_count, services_queried, services_failed}}
   */
  streamExceptions(params: ExceptionQueryParams): Observable<ExceptionStreamEvent> {
    const qp = new URLSearchParams({
      environment: params.environment,
      from_time: params.from_time,
      to_time: params.to_time,
      limit: params.limit.toString(),
    });
    if (params.device_id) qp.set('device_id', params.device_id);

    const url = `${this.diagnosticUrl}/diagnostics/api/v1/app-insights/exceptions/stream?${qp.toString()}`;

    // Build headers — native fetch is used for SSE so auth headers are set manually
    // (Angular HttpClient interceptors do not apply here).
    const headers: Record<string, string> = {
      'OASIS-API-KEY': environment.oasisApiKey,
      'X-Correlation-ID': this.telemetry.correlationId,
    };
    const token = this.cache.authToken;
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return new Observable<ExceptionStreamEvent>(subscriber => {
      const controller = new AbortController();

      fetch(url, { headers, signal: controller.signal })
        .then(response => {
          if (!response.body) { subscriber.complete(); return; }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          const read = (): void => {
            reader.read().then(({ done, value }) => {
              if (done) { subscriber.complete(); return; }

              buffer += decoder.decode(value, { stream: true });
              let idx: number;
              while ((idx = buffer.indexOf('\n\n')) !== -1) {
                const msg = buffer.slice(0, idx);
                buffer = buffer.slice(idx + 2);
                let eventType: ExceptionStreamEventType = 'chunk';
                let dataStr = '';
                for (const line of msg.split('\n')) {
                  if (line.startsWith('event: ')) eventType = line.slice(7).trim() as ExceptionStreamEventType;
                  else if (line.startsWith('data: ')) dataStr = line.slice(6);
                }
                if (dataStr) {
                  try { subscriber.next({ type: eventType, data: JSON.parse(dataStr) }); } catch { /* skip malformed */ }
                }
              }
              read();
            }).catch(err => {
              if (err?.name !== 'AbortError') subscriber.error(new Error(err?.message ?? 'Stream read error'));
            });
          };

          read();
        })
        .catch(err => {
          if (err?.name !== 'AbortError') subscriber.error(new Error(err?.message ?? 'Stream fetch error'));
        });

      return () => controller.abort();
    });
  }

  /**
   * Fetch the end-to-end operation trace for an exception from the DiagnosticService.
   */
  getExceptionTrace(
    operationId: string,
    environment: string,
    excTimestamp?: string,
  ): Observable<ExceptionTraceResponse> {
    const params: Record<string, string> = { operation_id: operationId, environment };
    if (excTimestamp) params['exc_timestamp'] = excTimestamp;
    const url = `${this.diagnosticUrl}/diagnostics/api/v1/app-insights/exceptions/trace`;
    return this.https.get<ExceptionTraceResponse>(url, { params }).pipe(
      catchError(e => this.handleError(e))
    );
  }
}


